import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowRight, CheckCircle2, Clock, FileUp, MapPin, Phone,
  Shield, Sparkles,
} from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Iter 152 · Shared 30-second film reel.
 *
 * Extracted from ``WatchDemoPage`` so the landing hero can embed
 * a muted, no-chrome preview of the same 6-scene timeline without
 * duplicating any scene markup or timing logic.
 *
 * Public API:
 *
 *   TOTAL              — number of seconds in the film (30)
 *   SCENES             — array of { id, start, dur }
 *   useFilmClock({ playing }) → elapsed
 *     Drives a RAF-locked wall clock that auto-loops at TOTAL.
 *     Pausing (``playing=false``) freezes the clock.
 *   FilmReel({ elapsed, mini, ctaEnabled })
 *     Pure render of the 6 scenes + backdrop + optional progress
 *     bar. ``mini=true`` scales tighter typography, hides progress
 *     ticks, and mutes the outcome-scene CTAs (they'd be
 *     misleading in a preview).
 */

export const TOTAL = 30;

export const SCENES = [
  { id: 'problem',   start: 0,  dur: 5 },
  { id: 'drop',      start: 5,  dur: 5 },
  { id: 'extract',   start: 10, dur: 5 },
  { id: 'match',     start: 15, dur: 5 },
  { id: 'accept',    start: 20, dur: 5 },
  { id: 'outcome',   start: 25, dur: 5 },
];

/**
 * Shared clock. Auto-loops after TOTAL seconds with an ~0.8s beat
 * between loops so the transition is felt, not jarring.
 */
export function useFilmClock({ playing = true } = {}) {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(Date.now());
  const rafRef = useRef(null);

  useEffect(() => {
    if (!playing) return;
    startRef.current = Date.now() - elapsed * 1000;

    const tick = () => {
      const e = Math.min(TOTAL, (Date.now() - startRef.current) / 1000);
      setElapsed(e);
      if (e >= TOTAL) return;
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing]);

  useEffect(() => {
    if (elapsed >= TOTAL && playing) {
      const t = setTimeout(() => {
        setElapsed(0);
        startRef.current = Date.now();
      }, 800);
      return () => clearTimeout(t);
    }
  }, [elapsed, playing]);

  return [elapsed, setElapsed, startRef];
}

export function currentScene(elapsed) {
  return SCENES.find(
    (s) => elapsed >= s.start && elapsed < s.start + s.dur,
  ) || SCENES[SCENES.length - 1];
}

export function FilmReel({
  elapsed = 0,
  mini = false,
  ctaEnabled = true,
  showProgress = true,
  startRefKey = 0,
}) {
  const activeScene = currentScene(elapsed);

  // Typography scale — mini variants keep the composition legible
  // inside a landing-hero card without cropping.
  const t = (klass, miniKlass) => (mini ? miniKlass : klass);

  return (
    <div
      className={`relative w-full aspect-[21/9] rounded-2xl overflow-hidden bg-[#0a0e1a] ${mini ? '' : 'shadow-[0_20px_120px_-30px_rgba(59,130,246,0.35)]'} border border-slate-800/60`}
      data-testid={mini ? 'film-reel-mini' : 'film-reel'}
      key={elapsed < 0.1 ? `loop-${startRefKey}` : 'run'}
    >
      <style>{keyframes}</style>

      {/* Vignette */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_50%,rgba(0,0,0,0.6)_100%)] z-30" />

      {/* Grid backdrop */}
      <div className="absolute inset-0 opacity-[0.06] z-0" style={{
        backgroundImage: 'linear-gradient(rgba(148,163,184,1) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,1) 1px, transparent 1px)',
        backgroundSize: mini ? '20px 20px' : '40px 40px',
      }} />

      {/* Scene 1 · Problem */}
      <SceneWrap active={activeScene.id === 'problem'}>
        <div className="h-full w-full flex items-center justify-center p-8">
          <div className="text-center max-w-2xl">
            <div className={`${t('text-xs md:text-sm', 'text-[9px]')} uppercase tracking-[0.3em] text-slate-500 mb-4 anim-fade-up delay-0`}>
              Hospital discharge, today
            </div>
            <div className={`${t('text-5xl md:text-7xl', 'text-3xl md:text-4xl')} font-semibold tracking-tight anim-fade-up delay-200`}>
              <span className="text-rose-400 tabular-nums">3.4 hours</span>
            </div>
            <div className={`mt-4 ${t('text-lg md:text-2xl', 'text-xs md:text-sm')} text-slate-300 anim-fade-up delay-500`}>
              to place a single home-health referral
            </div>
            {!mini && (
              <div className="mt-8 flex flex-wrap justify-center gap-2 text-xs anim-fade-up delay-1000">
                {[
                  'Fax the summary',
                  'Call 12 agencies',
                  'Cross-check licences',
                  'Wait on hold',
                  'Write everything twice',
                ].map((s, i) => (
                  <span key={s} className="rounded-full border border-slate-800 bg-slate-950/60 text-slate-500 px-3 py-1"
                    style={{ animationDelay: `${1200 + i * 120}ms` }}>
                    {s}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </SceneWrap>

      {/* Scene 2 · Drop */}
      <SceneWrap active={activeScene.id === 'drop'}>
        <div className="h-full w-full flex items-center justify-center p-8">
          <div className="w-full max-w-2xl">
            <div className={`${t('text-xs', 'text-[9px]')} uppercase tracking-[0.3em] text-slate-500 mb-4 text-center anim-fade-up delay-0`}>
              Drop the discharge summary
            </div>

            <div className="relative">
              <div className={`absolute left-1/2 -translate-x-1/2 ${mini ? '-top-16' : '-top-24'} anim-file-fall z-10`}>
                <div className={`${mini ? 'w-24 px-2 py-1.5' : 'w-40 px-4 py-3'} rounded-lg bg-white shadow-2xl text-slate-800 text-xs`}>
                  <div className={`font-semibold text-slate-900 ${mini ? 'text-[9px]' : ''}`}>discharge_2645.pdf</div>
                  <div className="mt-1 space-y-1">
                    <div className="h-1 bg-slate-200 rounded w-full" />
                    <div className="h-1 bg-slate-200 rounded w-5/6" />
                    <div className="h-1 bg-slate-200 rounded w-2/3" />
                    <div className="h-1 bg-slate-200 rounded w-3/4" />
                  </div>
                </div>
              </div>

              <div className={`${mini ? 'mt-10 p-4' : 'mt-16 p-8'} relative rounded-2xl border-2 border-dashed border-indigo-500/50 bg-indigo-500/5 anim-fade-up delay-0`}>
                <div className="flex items-center gap-4">
                  <div className={`rounded-xl bg-indigo-500/20 ${mini ? 'p-2' : 'p-3'}`}>
                    <FileUp className={`${mini ? 'h-4 w-4' : 'h-6 w-6'} text-indigo-300`} />
                  </div>
                  <div className="flex-1">
                    <div className={`${t('text-base md:text-lg', 'text-xs')} font-medium text-indigo-200`}>Copy from EHR</div>
                    <div className={`${mini ? 'text-[9px]' : 'text-xs'} text-slate-500 mt-0.5`}>
                      <Sparkles className="inline h-3 w-3 mr-1 -mt-0.5" />
                      BAA-locked · PHI scrubbed · auto-fills empty fields
                    </div>
                  </div>
                </div>
                <div className="absolute inset-0 rounded-2xl anim-flash pointer-events-none" />
              </div>
            </div>
          </div>
        </div>
      </SceneWrap>

      {/* Scene 3 · Extract */}
      <SceneWrap active={activeScene.id === 'extract'}>
        <div className={`h-full w-full grid grid-cols-2 gap-6 ${mini ? 'p-4' : 'p-6 md:p-10'}`}>
          <div className={`rounded-xl bg-slate-950/60 border border-slate-800 ${mini ? 'p-2' : 'p-4'} overflow-hidden`}>
            <div className={`${mini ? 'text-[8px]' : 'text-[10px]'} uppercase tracking-wider text-slate-500 mb-2`}>Source · discharge_2645.pdf</div>
            <div className={`${mini ? 'text-[9px]' : 'text-[11px] md:text-xs'} leading-relaxed text-slate-400 space-y-1`}>
              <span className="anim-fade-up delay-0 block">72yo F, s/p CABG POD#4, admitted for </span>
              <span className="anim-fade-up delay-200 block"><mark className="bg-indigo-500/25 text-indigo-200 px-1 rounded">acute exacerbation of CHF</mark>, EF 25%.</span>
              <span className="anim-fade-up delay-400 block">PMH: <mark className="bg-indigo-500/25 text-indigo-200 px-1 rounded">T2DM</mark>, CKD stage 3.</span>
              <span className="anim-fade-up delay-600 block">Discharge home to <mark className="bg-indigo-500/25 text-indigo-200 px-1 rounded">Trenton, NJ</mark></span>
              <span className="anim-fade-up delay-800 block">Insurance: <mark className="bg-indigo-500/25 text-indigo-200 px-1 rounded">Medicare Advantage</mark></span>
              {!mini && <span className="anim-fade-up delay-1000 block">Awaiting home health for wound VAC + diuretic titration.</span>}
            </div>
          </div>

          <div className="space-y-2">
            <div className={`${mini ? 'text-[8px]' : 'text-[10px]'} uppercase tracking-wider text-slate-500`}>Extracted · Vertex AI</div>
            <FieldRow label="Primary diagnosis" value="Acute-on-chronic heart failure" delay={200} mini={mini} />
            <FieldRow label="ICD-10" chips={['I50.9', 'E11.9', 'N18.30']} delay={500} mini={mini} />
            <div className="grid grid-cols-2 gap-2">
              <FieldRow label="State" value="NJ" delay={900} compact mini={mini} />
              <FieldRow label="City" value="Trenton" delay={1000} compact mini={mini} />
            </div>
            {!mini && <FieldRow label="Insurance" value="Medicare Advantage" delay={1200} mini={mini} />}
            {!mini && <FieldRow label="Care type" value="home_health" delay={1400} mini={mini} />}
          </div>
        </div>
      </SceneWrap>

      {/* Scene 4 · Match */}
      <SceneWrap active={activeScene.id === 'match'}>
        <div className={`h-full w-full grid grid-cols-5 gap-4 ${mini ? 'p-4' : 'p-6 md:p-10'}`}>
          <div className={`col-span-2 rounded-xl bg-gradient-to-b from-slate-900 to-slate-950 border border-slate-800 ${mini ? 'p-3' : 'p-5'} anim-fade-up delay-0`}>
            <div className={`${mini ? 'text-[8px]' : 'text-[10px]'} uppercase tracking-wider text-slate-500`}>Referral #2645</div>
            <div className={`${t('mt-2 text-lg', 'mt-1 text-sm')} font-semibold`}>Mrs. J.</div>
            <div className={`mt-1 ${t('text-sm', 'text-[10px]')} text-slate-400`}>Acute-on-chronic CHF</div>
            <div className="mt-3 flex flex-wrap gap-1 text-[10px]">
              {['I50.9', 'E11.9', 'N18.30'].map(c => (
                <span key={c} className={`font-mono rounded-full bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 ${mini ? 'text-[8px] px-1.5 py-0' : 'px-2 py-0.5'}`}>
                  {c}
                </span>
              ))}
            </div>
            {!mini && (
              <div className="mt-4 grid grid-cols-2 gap-y-2 text-xs">
                <span className="text-slate-500">Location</span>  <span className="text-slate-200">Trenton, NJ</span>
                <span className="text-slate-500">Discharge</span> <span className="text-slate-200">Jul 18, 2026</span>
                <span className="text-slate-500">Urgency</span>   <span className="text-amber-300">Urgent</span>
              </div>
            )}
          </div>

          <div className="col-span-3 flex flex-col">
            <div className={`${mini ? 'text-[8px]' : 'text-[10px]'} uppercase tracking-wider text-slate-500 mb-2 anim-fade-up delay-0`}>
              Matching engine · 46 candidates ranked in <span className="text-emerald-300">184 ms</span>
            </div>
            <div className={mini ? 'space-y-1' : 'space-y-2'}>
              {[
                { name: 'Angela R., RN · Home Health Care', score: 94, dist: '2.1 mi', tag: 'Best fit', winner: true },
                { name: 'Marcus L., RN',                      score: 91, dist: '3.4 mi', tag: 'CHF specialty' },
                { name: 'Priya S., RN · Bilingual (Spanish)', score: 88, dist: '5.0 mi', tag: 'Diuretic protocol' },
                { name: 'Dana K., NP',                         score: 82, dist: '6.2 mi', tag: 'Medicare-in-network' },
              ].slice(0, mini ? 3 : 4).map((c, i) => (
                <div key={c.name}
                  className={`rounded-lg border ${mini ? 'p-1.5' : 'p-3'} flex items-center justify-between anim-tile-in ${c.winner ? 'border-emerald-500/60 bg-emerald-500/10' : 'border-slate-800 bg-slate-950/60'}`}
                  style={{ animationDelay: `${300 + i * 250}ms` }}
                >
                  <div className="min-w-0">
                    <div className={`${mini ? 'text-[10px]' : 'text-sm'} text-slate-200 truncate`}>{c.name}</div>
                    <div className={`${mini ? 'text-[8px]' : 'text-[10px]'} text-slate-500 mt-0.5 flex items-center gap-1.5`}>
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{c.dist}</span>
                      <span>·</span>
                      <span>{c.tag}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`${mini ? 'text-sm' : 'text-lg'} font-semibold tabular-nums ${c.winner ? 'text-emerald-300' : 'text-slate-300'}`}>{c.score}</span>
                    {c.winner && <CheckCircle2 className={`${mini ? 'h-3.5 w-3.5' : 'h-5 w-5'} text-emerald-400`} />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </SceneWrap>

      {/* Scene 5 · Accept */}
      <SceneWrap active={activeScene.id === 'accept'}>
        <div className={`h-full w-full flex items-center justify-center ${mini ? 'p-3 gap-4' : 'p-6 md:p-10 gap-8'}`}>
          <div className={`relative ${mini ? 'w-32 h-56 border-2 rounded-3xl' : 'w-56 h-[380px] border-4 rounded-[36px]'} border-slate-800 bg-slate-950 shadow-2xl overflow-hidden anim-fade-up delay-0`}>
            <div className={`absolute top-0 left-1/2 -translate-x-1/2 ${mini ? 'h-3 w-14' : 'h-5 w-24'} bg-slate-950 rounded-b-2xl`} />
            <div className={`${mini ? 'pt-5 px-2 space-y-1.5' : 'pt-8 px-3 space-y-2'}`}>
              <div className={`${mini ? 'text-[8px]' : 'text-[9px]'} text-center text-slate-500 uppercase tracking-widest`}>CaraLoom</div>

              <div className={`rounded-2xl rounded-bl-md bg-slate-800/80 text-slate-200 ${mini ? 'text-[8px] p-1.5' : 'text-[11px] p-3'} anim-fade-up delay-300`}>
                New referral · <b>Mrs. J.</b> · CHF discharge{!mini && ' · Trenton, NJ'}.
                {!mini && <> Est. 2 visits/wk × 4 wks · <b>$180/visit</b>.</>}
                <div className={`mt-1 ${mini ? 'text-[7px]' : 'text-[9px]'} text-indigo-300`}>Reply Y within 10 min to accept</div>
              </div>

              <div className="flex justify-end anim-fade-up delay-1500">
                <div className={`rounded-2xl rounded-br-md bg-indigo-500 text-white font-medium ${mini ? 'text-[10px] px-2 py-1' : 'text-sm px-4 py-2'}`}>
                  Y
                </div>
              </div>

              <div className={`rounded-2xl rounded-bl-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 ${mini ? 'text-[8px] p-1.5' : 'text-[11px] p-3'} anim-fade-up delay-2500`}>
                <CheckCircle2 className={`inline ${mini ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'} mr-1 -mt-0.5`} />
                <b>Placed.</b>{!mini && ' Visit scheduled Jul 19, 2 pm.'}
              </div>
            </div>
          </div>

          {!mini && (
            <div className="max-w-xs anim-fade-up delay-1000">
              <div className="text-xs uppercase tracking-[0.25em] text-slate-500 mb-2">One tap accept</div>
              <div className="text-2xl md:text-3xl font-semibold leading-tight">
                The clinician&rsquo;s phone is<br />the whole placement flow.
              </div>
              <div className="mt-3 text-sm text-slate-400">
                Signed one-tap links, offer TTL, auto-escalation.
                Zero portals to log into.
              </div>
            </div>
          )}
          {mini && (
            <div className="max-w-[10rem] anim-fade-up delay-1000">
              <div className="text-[8px] uppercase tracking-[0.25em] text-slate-500 mb-1">One tap accept</div>
              <div className="text-sm font-semibold leading-tight">
                The clinician&rsquo;s phone is the whole flow.
              </div>
            </div>
          )}
        </div>
      </SceneWrap>

      {/* Scene 6 · Outcome */}
      <SceneWrap active={activeScene.id === 'outcome'}>
        <div className="h-full w-full flex items-center justify-center p-6">
          <div className="text-center max-w-3xl">
            <div className={`${t('text-xs md:text-sm', 'text-[9px]')} uppercase tracking-[0.3em] text-slate-500 mb-4 anim-fade-up delay-0`}>
              Same discharge · with CaraLoom
            </div>
            <div className={`${t('text-6xl md:text-8xl', 'text-3xl md:text-5xl')} font-semibold tracking-tight anim-fade-up delay-200`}>
              <span className="text-emerald-300 tabular-nums">4 min 12 sec</span>
            </div>
            <div className={`mt-4 flex justify-center ${mini ? 'gap-4' : 'gap-6 md:gap-10'} ${t('text-xs md:text-sm', 'text-[9px]')} text-slate-400 anim-fade-up delay-1200`}>
              <Metric icon={Clock}  value="94×" note="faster placement" mini={mini} />
              <Metric icon={Shield} value="HIPAA · BAA" note="BAA-locked LLMs" mini={mini} />
              <Metric icon={Phone}  value="1-tap"      note="clinician accept" mini={mini} />
            </div>

            {ctaEnabled && !mini && (
              <div className="mt-10 flex justify-center gap-3 anim-fade-up delay-2000">
                <Link
                  to="/planner/dashboard"
                  className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 hover:bg-slate-200 px-6 py-3 text-sm font-medium"
                  data-testid="watch-cta-demo"
                >
                  Try the live demo
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href="mailto:hello@caraloom.com?subject=CaraLoom · book a demo"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-700 hover:bg-slate-900 px-6 py-3 text-sm text-slate-200"
                  data-testid="watch-cta-email"
                >
                  Book a walkthrough
                </a>
              </div>
            )}
          </div>
        </div>
      </SceneWrap>

      {/* Progress bar (film only — mini shows a thinner variant) */}
      {showProgress && (
        <div className={`absolute bottom-0 left-0 right-0 ${mini ? 'h-0.5' : 'h-1'} bg-slate-800/60 z-40`}>
          <div
            className="h-full bg-gradient-to-r from-indigo-400 to-emerald-400"
            style={{ width: `${(elapsed / TOTAL) * 100}%`, transition: 'width 100ms linear' }}
          />
        </div>
      )}

      {!mini && (
        <div className="absolute bottom-2 left-0 right-0 flex justify-between px-6 text-[9px] text-slate-500 tracking-widest uppercase z-40">
          {SCENES.map((s) => (
            <span key={s.id} className={activeScene.id === s.id ? 'text-slate-300' : ''}>
              {s.id}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

function SceneWrap({ active, children }) {
  return (
    <div
      className={`absolute inset-0 transition-opacity duration-700 ${active ? 'opacity-100 z-20' : 'opacity-0 z-10 pointer-events-none'}`}
    >
      {active && children}
    </div>
  );
}

function FieldRow({ label, value, chips, delay = 0, compact, mini }) {
  return (
    <div
      className={`anim-fade-up rounded-lg border border-slate-800 bg-slate-950/60 ${mini ? 'p-2' : 'p-3'}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className={`${mini ? 'text-[8px]' : 'text-[10px]'} uppercase tracking-wider text-slate-500`}>{label}</div>
      {chips ? (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {chips.map((c, i) => (
            <span
              key={c}
              className={`anim-chip font-mono ${mini ? 'text-[9px] px-1.5 py-0' : 'text-[11px] px-2 py-0.5'} rounded-full bg-indigo-500/15 text-indigo-300 border border-indigo-500/40`}
              style={{ animationDelay: `${delay + 200 + i * 220}ms` }}
            >
              {c}
            </span>
          ))}
        </div>
      ) : (
        <div className={`mt-1 text-slate-200 ${compact ? (mini ? 'text-[10px]' : 'text-sm') : (mini ? 'text-xs' : 'text-base')} font-medium`}>
          {value}
        </div>
      )}
    </div>
  );
}

function Metric({ icon: Icon, value, note, mini }) {
  return (
    <div className="flex flex-col items-center">
      <Icon className={`${mini ? 'h-3.5 w-3.5' : 'h-5 w-5'} mb-1 text-slate-500`} />
      <div className={`text-slate-100 font-semibold ${mini ? 'text-[11px]' : 'text-base md:text-lg'} tabular-nums`}>{value}</div>
      <div className={`${mini ? 'text-[7px]' : 'text-[10px]'} uppercase tracking-wider text-slate-500 mt-0.5`}>{note}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Keyframes — inline so both mini and full share one style tag.
// -----------------------------------------------------------------------------
const keyframes = `
  @keyframes anim-fade-up {
    0%   { opacity: 0; transform: translateY(12px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  .anim-fade-up { animation: anim-fade-up 700ms cubic-bezier(0.16, 1, 0.3, 1) both; }
  .delay-0    { animation-delay: 0ms; }
  .delay-200  { animation-delay: 200ms; }
  .delay-300  { animation-delay: 300ms; }
  .delay-400  { animation-delay: 400ms; }
  .delay-500  { animation-delay: 500ms; }
  .delay-600  { animation-delay: 600ms; }
  .delay-800  { animation-delay: 800ms; }
  .delay-1000 { animation-delay: 1000ms; }
  .delay-1200 { animation-delay: 1200ms; }
  .delay-1400 { animation-delay: 1400ms; }
  .delay-1500 { animation-delay: 1500ms; }
  .delay-2000 { animation-delay: 2000ms; }
  .delay-2500 { animation-delay: 2500ms; }

  @keyframes anim-chip {
    0%   { opacity: 0; transform: scale(0.6); }
    60%  { opacity: 1; transform: scale(1.08); }
    100% { opacity: 1; transform: scale(1); }
  }
  .anim-chip { animation: anim-chip 500ms cubic-bezier(0.34, 1.56, 0.64, 1) both; }

  @keyframes anim-tile-in {
    0%   { opacity: 0; transform: translateX(20px); }
    100% { opacity: 1; transform: translateX(0); }
  }
  .anim-tile-in { animation: anim-tile-in 600ms cubic-bezier(0.16, 1, 0.3, 1) both; }

  @keyframes anim-file-fall {
    0%   { opacity: 0; transform: translate(-50%, -180px) rotate(-6deg); }
    50%  { opacity: 1; transform: translate(-50%, -20px) rotate(0deg); }
    70%  { transform: translate(-50%, 40px) rotate(2deg); }
    85%  { transform: translate(-50%, 30px) rotate(0deg); }
    100% { opacity: 1; transform: translate(-50%, 30px) rotate(0deg); }
  }
  .anim-file-fall { animation: anim-file-fall 1.6s cubic-bezier(0.34, 1.36, 0.5, 1) both; }

  @keyframes anim-flash {
    0%, 60% { background: transparent; }
    70%     { background: rgba(129, 140, 248, 0.35); }
    100%    { background: transparent; }
  }
  .anim-flash { animation: anim-flash 1.8s ease-out both; animation-delay: 0.2s; }
`;
